import type { SetStateAction } from 'jotai'
import type { Dispatch } from 'react'
import { useMemo } from 'react'
import { Switch as AriaSwitch } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { isMatching, P } from 'ts-pattern'
import Text from 'src/components/ui/Text'
import { getMembershipPlanDiscountPercentage } from 'src/features/Membership/utils/membershipPlanDiscount'
import { Interval } from 'src/services/apollo/generated/graphql'
import type { MembershipPlan } from 'src/services/helpers/membership'
import { useCurrency } from 'src/utils/currency'
import { cx } from 'src/utils/cx'
import { track } from 'src/utils/tracker'
import { GrowthEventName, useGrowthEvent } from '~/features/GrowthEvent/store'

export function MembershipPlansIntervalSwitch(
  props: {
    billingInterval: Interval
    setBillingInterval: Dispatch<SetStateAction<Interval>>
    saleLabelOnTopIfMsm?: boolean
    trackingArgs?: Record<string, any>
    hideSaleLabel?: boolean
    plans?: MembershipPlan[]
  } & StyleProps,
) {
  const {
    billingInterval,
    setBillingInterval,
    className,
    saleLabelOnTopIfMsm,
    trackingArgs,
    hideSaleLabel,
    plans,
  } = props
  const { t } = useTranslation(['membership', 'growth'])

  const classes = {
    intervalTabContainer:
      'mmd:w-screen max-w-[365px] p-0.5 flex bg-white dark:bg-black/50 ring-1 dark:ring-0 ring-black/5 rounded-lg shadow-md',
    intervalTabItem:
      'flex-1 mlg:w-[180px] h-8 mmd:h-9 px-4 msm:px-8 flex justify-center items-center max-mmd:text-sm font-medium relative data-[state=active]:bg-purple-700 dark:data-[state=active]:bg-purple-200 data-[state=active]:text-white dark:data-[state=active]:text-black rounded-md',
    intervalLabel:
      'whitespace-nowrap font-medium text-neutral-700 dark:text-neutral-300 data-[active]:text-purple-700 dark:data-[active]:text-purple-500',
  } as const

  const CHECKED_PLAN = Interval.Month
  const UNCHECKED_PLAN = Interval.Year

  const { ongoing } = useGrowthEvent(GrowthEventName.AugustEvent2025)
  const maxYearlyDiscount = useMaxYearlyDiscount(plans)

  return (
    <div
      className={cx('flex justify-center items-center gap-3 pb-4', className)}
    >
      <span className="flex justify-end w-0">
        <button
          data-active={billingInterval === Interval.Year ? '' : null}
          className={cx(
            classes.intervalLabel,
            'relative flex items-center gap-2',
          )}
          onClick={() => {
            track('member_plan_tab_click', {
              tab: Interval.Year,
              ...trackingArgs,
            })
            setBillingInterval(Interval.Year)
          }}
        >
          {isMatching(P.number.gt(0), maxYearlyDiscount) && (
            <span
              className={cx(
                'left-1/2 bottom-full px-2.5 py-0.5 text-sm font-normal whitespace-nowrap rounded-full transition',
                billingInterval === Interval.Year
                  ? 'text-white bg-purple-700 dark:bg-purple-600'
                  : 'bg-black/10 dark:bg-black/50',
                saleLabelOnTopIfMsm &&
                  'max-msm:absolute max-msm:-translate-x-1/2 max-msm:-translate-y-2',
                hideSaleLabel && 'hidden',
              )}
            >
              {t('membership:plans-page.plan-tab-promo-label', {
                discount: `${maxYearlyDiscount}%`,
              })}
              <i
                className={cx(
                  'absolute top-full left-1/2 -translate-x-2 block border-8 border-b-0 border-transparent transition',
                  billingInterval === Interval.Year
                    ? 'border-t-purple-700 dark:border-t-purple-600'
                    : 'border-t-black/10 dark:border-t-black/50',
                  saleLabelOnTopIfMsm ? 'msm:hidden' : 'hidden',
                )}
              />
            </span>
          )}
          {t('membership:payment.annually')}
        </button>
      </span>
      <AriaSwitch
        isSelected={billingInterval === CHECKED_PLAN}
        onChange={checked => {
          track('member_plan_tab_click', {
            tab: checked ? CHECKED_PLAN : UNCHECKED_PLAN,
            ...trackingArgs,
          })
          setBillingInterval(checked ? CHECKED_PLAN : UNCHECKED_PLAN)
        }}
        className="group flex w-14 h-8 p-0.5 ring-1 ring-purple-500 rounded-full shadow shadow-purple-500/50"
      >
        <span className="block h-full aspect-square bg-purple-600 dark:bg-purple-500 ring-1 ring-black/10 rounded-full shadow-md group-selected:translate-x-6 transition ease-in-out" />
      </AriaSwitch>
      <span className="flex justify-start w-0">
        <button
          data-active={billingInterval === Interval.Month ? '' : null}
          className={cx(
            classes.intervalLabel,
            'relative flex items-center gap-2',
          )}
          onClick={() => {
            track('member_plan_tab_click', {
              tab: Interval.Month,
              ...trackingArgs,
            })
            setBillingInterval(Interval.Month)
          }}
        >
          {t('membership:payment.monthly')}
          {ongoing && (
            <span
              className={cx(
                'left-1/2 bottom-full px-2.5 py-0.5 text-sm font-normal whitespace-nowrap rounded-full transition',
                billingInterval === Interval.Month
                  ? 'text-white bg-purple-700 dark:bg-purple-600'
                  : 'bg-black/10 dark:bg-black/50',
                saleLabelOnTopIfMsm &&
                  'max-msm:absolute max-msm:-translate-x-1/2 max-msm:-translate-y-2',
                hideSaleLabel && 'hidden',
              )}
            >
              {t('membership:plans-page.plan-tab-promo-label-monthly')}
              <i
                className={cx(
                  'absolute top-full left-1/2 -translate-x-2 block border-8 border-b-0 border-transparent transition',
                  billingInterval === Interval.Month
                    ? 'border-t-purple-700 dark:border-t-purple-600'
                    : 'border-t-black/10 dark:border-t-black/50',
                  saleLabelOnTopIfMsm ? 'msm:hidden' : 'hidden',
                )}
              />
            </span>
          )}
        </button>
      </span>
    </div>
  )
}

function useMaxYearlyDiscount(plans: MembershipPlan[] | undefined) {
  const [currency] = useCurrency()

  return useMemo(() => {
    if (!plans?.length) {
      return undefined
    }

    const discounts = plans
      .map(plan =>
        getMembershipPlanDiscountPercentage(
          plan.prices[Interval.Year][currency],
          Interval.Year,
        ),
      )
      .filter((discount): discount is number => discount != null)

    return discounts.length ? Math.max(...discounts) : undefined
  }, [currency, plans])
}
